import { Add, Person } from '@mui/icons-material';
import { Box, Chip, Grid, IconButton, Paper, Typography } from '@mui/material';
import { getBusinessExecutives } from 'lib/api/businessRelations';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import AssignExecutive from './Components/AssignExecutive';
import DeleteExecutiveDialog from './Components/DeleteExecutiveDialog';
import ExecutiveRow, { Executive } from './Components/ExecutiveRow';

interface ExecutivesProps {
  businessIdentifier: string;
}

export interface ExecutivesResponse {
  commercialExecutives: Executive[];
  collectionExecutives: Executive[];
}

type ExecutiveToDelete = {
  executive: Executive;
  type: 'commercial' | 'collection';
};

const Executives = (props: ExecutivesProps) => {
  const { businessIdentifier } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const canEdit = validatePermissions(
    user,
    'users_create_business_association',
  );
  const [openAsignExecutive, setOpenAsignExecutive] = useState(false);
  const [executives, setExecutives] = useState<ExecutivesResponse>({
    commercialExecutives: [],
    collectionExecutives: [],
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [executiveToDelete, setExecutiveToDelete] =
    useState<ExecutiveToDelete | null>(null);

  const fetchBusinessExecutives = async () => {
    try {
      if (!businessIdentifier) return;
      const response = await getBusinessExecutives(
        dispatch,
        businessIdentifier,
      );
      setExecutives(response);
    } catch (error) {
      console.error(error);
    }
  };

  const openAssignExecutive = () => {
    setOpenAsignExecutive(true);
  };

  const closeAssignExecutive = () => {
    setOpenAsignExecutive(false);
  };

  const handleDeleteClick = (
    executive: Executive,
    type: 'commercial' | 'collection',
  ) => {
    setExecutiveToDelete({ executive, type });
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setExecutiveToDelete(null);
  };

  useEffect(() => {
    void fetchBusinessExecutives();
  }, [businessIdentifier]);

  return (
    <>
      <Paper
        sx={{
          p: 3,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          position: 'relative',
          marginTop: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.2rem',
                lineHeight: 1.1,
              }}
            >
              Ejecutivos
            </Typography>
            <Chip
              label="Resumen"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '22px', fontSize: '0.75rem' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canEdit && (
              <IconButton
                size="small"
                onClick={openAssignExecutive}
                sx={{
                  padding: 1,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  },
                }}
              >
                <Add sx={{ fontSize: '1.1rem', color: 'success.main' }} />
              </IconButton>
            )}
          </Box>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {executives.commercialExecutives.length > 0 ? (
                executives.commercialExecutives.map((executive) => (
                  <ExecutiveRow
                    key={executive.id}
                    executive={executive}
                    type="commercial"
                    onDelete={() => handleDeleteClick(executive, 'commercial')}
                    canDelete={canEdit}
                  />
                ))
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                      borderRadius: '50%',
                      p: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '24px',
                      height: '24px',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.8rem', color: 'text.disabled' }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      color: 'text.disabled',
                      fontStyle: 'italic',
                    }}
                  >
                    Sin ejecutivos comerciales
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {executives.collectionExecutives.length > 0 ? (
                executives.collectionExecutives.map((executive) => (
                  <ExecutiveRow
                    key={executive.id}
                    executive={executive}
                    type="collection"
                    onDelete={() => handleDeleteClick(executive, 'collection')}
                    canDelete={canEdit}
                  />
                ))
              ) : (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(158, 158, 158, 0.1)',
                      borderRadius: '50%',
                      p: 0.4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '24px',
                      height: '24px',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.8rem', color: 'text.disabled' }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      lineHeight: 1.2,
                      color: 'text.disabled',
                      fontStyle: 'italic',
                    }}
                  >
                    Sin ejecutivos cobranza
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <AssignExecutive
        open={openAsignExecutive}
        onClose={closeAssignExecutive}
        businessIdentifier={businessIdentifier}
        executives={executives}
        refetchExecutives={fetchBusinessExecutives}
      />
      <DeleteExecutiveDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        executive={executiveToDelete?.executive || null}
        type={executiveToDelete?.type || null}
        businessIdentifier={businessIdentifier}
        onSuccess={fetchBusinessExecutives}
      />
    </>
  );
};

export default Executives;
